/**
 * Register Page
 *
 * Allows new users to create a company and admin account.
 * On successful registration, redirects to the dashboard.
 *
 * Features:
 * - Company and user registration form
 * - Form validation
 * - Password confirmation
 * - Error handling
 * - Link to login
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';

/**
 * Register Component
 *
 * Renders the registration form for new companies.
 */
function Register() {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  // Watch password for confirmation validation
  const password = watch('password');

  /**
   * Handle form submission
   * @param {Object} data - Form data
   */
  const onSubmit = async (data) => {
    setIsLoading(true);
    setError(null);

    try {
      // Call register from auth context
      await registerUser({
        inviteCode: data.inviteCode,
        companyName: data.companyName,
        name: data.name,
        email: data.email,
        password: data.password
      });

      // Redirect to dashboard on success
      navigate('/');
    } catch (err) {
      // Show error message
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-2xl font-bold">OMS</span>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-500">
              Sign in
            </Link>
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="ml-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md">
          <div className="space-y-4">
            {/* Invite Code */}
            <Input
              label="Invite Code"
              {...register('inviteCode', {
                required: 'Invite code is required'
              })}
              error={errors.inviteCode}
              placeholder=""
            />

            {/* Company Name */}
            <Input
              label="Company Name"
              {...register('companyName', {
                required: 'Company name is required',
                minLength: {
                  value: 2,
                  message: 'Company name must be at least 2 characters'
                }
              })}
              error={errors.companyName}
              placeholder=""
            />

            {/* User Name */}
            <Input
              label="Your Name"
              {...register('name', {
                required: 'Name is required',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters'
                }
              })}
              error={errors.name}
              placeholder=""
            />

            {/* Email */}
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              error={errors.email}
              placeholder=""
            />

            {/* Password */}
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                }
              })}
              error={errors.password}
              placeholder=""
            />

            {/* Confirm Password */}
            <Input
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === password || 'Passwords do not match'
              })}
              error={errors.confirmPassword}
              placeholder=""
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full mt-6"
            size="lg"
            loading={isLoading}
          >
            Create Account
          </Button>

          {/* Terms notice */}
          <p className="mt-4 text-xs text-center text-gray-500">
            By creating an account, you agree to our Terms of Service
            and Privacy Policy.
          </p>
        </form>
      </div>
    </div>
  );
}

export default Register;
